import { action, flow, makeObservable, observable } from 'mobx';

import { apiFetcher } from '../utils/fetch';
import { updateItems } from './utils';

const defaultPagination = {
  total: 0,
  page: 1,
  limit: 10,
  pages: 0,
  hasNextPage: false,
  hasPrevPage: false,
};

export default class Request {
  constructor() {
    this.selected = {};
    this.items = [];
    this.pagination = defaultPagination;
    this.query = {
      page: 1,
      limit: 10,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    };

    makeObservable(this, {
      selected: observable,
      items: observable,
      pagination: observable,
      query: observable,
      setSelected: action,
      fetch: flow,
      fetchOne: flow,
      create: flow,
      update: flow,
      uploadAttachment: flow,
      deleteAttachment: flow,
      updateStatus: flow,
      delete: flow,
    });
  }

  setSelected = (request) => (this.selected = request);

  *fetch(filters = {}) {
    try {
      const params = new URLSearchParams();
      this.query = { ...this.query, ...filters };

      Object.entries(this.query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });

      const queryString = params.toString();
      const response = yield apiFetcher().get(`/requests${queryString ? `?${queryString}` : ''}`);
      const requests = (Array.isArray(response.data) ? response.data : response.data?.data || [])
        .filter((request) => !request.archivedAt);

      this.items = requests;
      if (response.data?.pagination) {
        this.pagination = response.data.pagination;
      } else {
        this.pagination = {
          ...defaultPagination,
          total: requests.length,
          limit: requests.length || defaultPagination.limit,
          pages: requests.length ? 1 : 0,
        };
      }

      return { status: response.status, data: response.data };
    } catch (error) {
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  *fetchOne(requestId) {
    try {
      const response = yield apiFetcher().get(`/requests/${requestId}`);
      const request = response.data;

      this.selected = request;
      if (request?._id || request?.id) {
        this.items = this.items.map((item) => (
          (item._id || item.id) === requestId ? { ...item, ...request } : item
        ));
      }

      return { status: response.status, data: request };
    } catch (error) {
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  *create(request) {
    try {
      const response = yield apiFetcher().post('/requests', request);
      const createdRequest = response.data;

      if (createdRequest?._id) {
        this.items = updateItems(createdRequest, this.items);
      } else if (createdRequest?.id) {
        this.items = [...this.items, createdRequest];
      }

      return { status: response.status, data: createdRequest };
    } catch (error) {
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  *update(requestId, request) {
    try {
      const response = yield apiFetcher().put(`/requests/${requestId}`, request);
      const updatedRequest = response.data || request;

      this.items = this.items.map((item) => (
        (item._id || item.id) === requestId ? { ...item, ...updatedRequest } : item
      ));

      if ((this.selected?._id || this.selected?.id) === requestId) {
        this.selected = { ...this.selected, ...updatedRequest };
      }

      return { status: response.status, data: updatedRequest };
    } catch (error) {
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  *uploadAttachment(requestId, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = yield apiFetcher().post(
        `/requests/${requestId}/attachment`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const updatedRequest = response.data;
      this.items = this.items.map((request) => (
        (request._id || request.id) === requestId ? { ...request, ...updatedRequest } : request
      ));
      if ((this.selected?._id || this.selected?.id) === requestId) {
        this.selected = { ...this.selected, ...updatedRequest };
      }

      return { status: response.status, data: response.data };
    } catch (error) {
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  *deleteAttachment(requestId, attachmentId) {
    try {
      const response = yield apiFetcher().delete(`/requests/${requestId}/attachments/${attachmentId}`);
      const updatedRequest = response.data;

      this.items = this.items.map((request) => (
        (request._id || request.id) === requestId ? { ...request, ...updatedRequest } : request
      ));
      if ((this.selected?._id || this.selected?.id) === requestId) {
        this.selected = { ...this.selected, ...updatedRequest };
      }

      return { status: response.status, data: updatedRequest };
    } catch (error) {
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  *updateStatus(requestId, status) {
    try {
      const response = yield apiFetcher().patch(`/requests/${requestId}/status`, { status });
      const updatedRequest = response.data;

      this.items = this.items.map((request) => (
        (request._id || request.id) === requestId ? { ...request, ...(updatedRequest || {}), status } : request
      ));

      return { status: response.status, data: updatedRequest };
    } catch (error) {
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }

  *delete(requestId) {
    try {
      const response = yield apiFetcher().delete(`/requests/${requestId}`);
      this.items = this.items.filter((request) => (request._id || request.id) !== requestId);

      return { status: response.status, data: response.data };
    } catch (error) {
      return {
        status: error?.response?.status,
        data: error?.response?.data,
      };
    }
  }
}
